import { FormLayout, Layout, Card, BlockStack, Text } from '@shopify/polaris'

import { Controller, useFormContext } from 'react-hook-form'
import { useIsMutating } from '@tanstack/react-query'
import { Disabled } from '../shared/Disabled'
import { ColorPicker } from '../shared/ColorPicker'

const DesignSettings = () => {
  const { control, watch } = useFormContext()
  const isMutatingSettings = useIsMutating({ mutationKey: ['settings'] })

  const primaryColour = watch('primaryColour')
  const textColour = watch('textColour')

  const year = new Date().getFullYear()

  return (
    <Layout.AnnotatedSection
      id="designSettings"
      title="Design settings"
      description="Customize the look and feel of the calendar to match your storefront."
    >
      <Card>
        <Disabled isDisabled={!!isMutatingSettings}>
          <BlockStack gap="600">
            <BlockStack gap="200">
              <Text variant="headingSm" fontWeight="medium" as="h3">
                Accent color
              </Text>
              <FormLayout>
                <FormLayout.Group>
                  <Controller
                    name="primaryColour"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ColorPicker
                        label="Background"
                        placeholder={'#4d4d4d'}
                        defaultColor={'#4d4d4d'}
                        color={value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <Controller
                    name="textColour"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ColorPicker
                        label="Text"
                        placeholder={'#ffffff'}
                        defaultColor={'#ffffff'}
                        color={value}
                        onChange={onChange}
                      />
                    )}
                  />
                </FormLayout.Group>
              </FormLayout>
            </BlockStack>
            <BlockStack gap="200">
              <Text variant="headingSm" fontWeight="medium" as="h3">
                Storefront preview
              </Text>
              <div className="ssdp__instance ssdp--align-left">
                <div
                  className="ssdp__container"
                  id="ssdp-datepicker-AZ0ZZb25PMzB3ME9rc__25cde6fb-2c56-4784-8863-29efeb5ed965"
                >
                  <div className="ssdp__wrapper" style={{ height: '290px' }}>
                    <div className="ssdp__datepicker">
                      <div
                        className="ssdp__controls"
                        style={{
                          backgroundColor: primaryColour
                            ? primaryColour
                            : '#4d4d4d',
                          color: textColour ? textColour : '#ffffff',
                        }}
                      >
                        <div className="ssdp__controls__prev">&lt;</div>
                        <div className="ssdp__controls__label">
                          <span>January {year}</span>
                        </div>
                        <div className="ssdp__controls__next">&gt;</div>
                      </div>
                      <table className="ssdp__calendar">
                        <thead>
                          <th>S</th>
                          <th>M</th>
                          <th>T</th>
                          <th>W</th>
                          <th>T</th>
                          <th>F</th>
                          <th>S</th>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="empty"></td>
                            <td className="empty"></td>
                            <td className="empty"></td>
                            <td className="empty"></td>
                            <td data-date="January 1, 2026">
                              <span>1</span>
                            </td>
                            <td data-date="January 2, 2026">
                              <span>2</span>
                            </td>
                            <td data-date="January 3, 2026">
                              <span>3</span>
                            </td>
                          </tr>
                          <tr>
                            <td data-date="January 4, 2026">
                              <span>4</span>
                            </td>
                            <td data-date="January 5, 2026">
                              <span>5</span>
                            </td>
                            <td data-date="January 6, 2026">
                              <span>6</span>
                            </td>
                            <td
                              data-date="January 7, 2026"
                              className="selected"
                              style={{
                                backgroundColor: primaryColour
                                  ? primaryColour
                                  : '#4d4d4d',
                                color: textColour ? textColour : '#ffffff',
                              }}
                            >
                              <span>7</span>
                            </td>
                            <td data-date="January 8, 2026">
                              <span>8</span>
                            </td>
                            <td data-date="January 9, 2026">
                              <span>9</span>
                            </td>
                            <td data-date="January 10, 2026">
                              <span>10</span>
                            </td>
                          </tr>
                          <tr>
                            <td data-date="January 11, 2026">
                              <span>11</span>
                            </td>
                            <td data-date="January 12, 2026">
                              <span>12</span>
                            </td>
                            <td data-date="January 13, 2026" className="">
                              <span>13</span>
                            </td>
                            <td
                              data-date="January 14, 2026"
                              className="disabled"
                            >
                              <span>14</span>
                            </td>
                            <td
                              data-date="January 15, 2026"
                              className="disabled"
                            >
                              <span>15</span>
                            </td>
                            <td
                              data-date="January 16, 2026"
                              className="disabled"
                            >
                              <span>16</span>
                            </td>
                            <td
                              data-date="January 17, 2026"
                              className="disabled"
                            >
                              <span>17</span>
                            </td>
                          </tr>
                          <tr>
                            <td
                              data-date="January 18, 2026"
                              className="disabled"
                            >
                              <span>18</span>
                            </td>
                            <td
                              data-date="January 19, 2026"
                              className="disabled"
                            >
                              <span>19</span>
                            </td>
                            <td
                              data-date="January 20, 2026"
                              className="disabled"
                            >
                              <span>20</span>
                            </td>
                            <td
                              data-date="January 21, 2026"
                              className="disabled"
                            >
                              <span>21</span>
                            </td>
                            <td
                              data-date="January 22, 2026"
                              className="disabled"
                            >
                              <span>22</span>
                            </td>
                            <td
                              data-date="January 23, 2026"
                              className="disabled"
                            >
                              <span>23</span>
                            </td>
                            <td
                              data-date="January 24, 2026"
                              className="disabled"
                            >
                              <span>24</span>
                            </td>
                          </tr>
                          <tr>
                            <td
                              data-date="January 25, 2026"
                              className="disabled"
                            >
                              <span>25</span>
                            </td>
                            <td
                              data-date="January 26, 2026"
                              className="disabled"
                            >
                              <span>26</span>
                            </td>
                            <td
                              data-date="January 27, 2026"
                              className="disabled"
                            >
                              <span>27</span>
                            </td>
                            <td
                              data-date="January 28, 2026"
                              className="disabled"
                            >
                              <span>28</span>
                            </td>
                            <td
                              data-date="January 29, 2026"
                              className="disabled"
                            >
                              <span>29</span>
                            </td>
                            <td
                              data-date="January 30, 2026"
                              className="disabled"
                            >
                              <span>30</span>
                            </td>
                            <td
                              data-date="January 31, 2026"
                              className="disabled"
                            >
                              <span>31</span>
                            </td>
                          </tr>
                          <tr></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </BlockStack>
          </BlockStack>
        </Disabled>
      </Card>
    </Layout.AnnotatedSection>
  )
}

export { DesignSettings }
